/**
 * Utilities per la gestione delle date
 * Centralizza tutte le funzioni di formatazione e manipolazione date
 */

/**
 * Formatta una data in formato italiano (dd/mm/yyyy)
 * @param {string|Date} dateString - La data da formattare
 * @returns {string} - Data formattata o stringa vuota se non valida
 */
export const formatDate = (dateString) => {
  if (!dateString) return "";
  
  // Supporta diversi formati di data
  try {
    const date = new Date(dateString);
    if (isNaN(date.getTime())) {
      // Se non è una data valida, prova a gestire il formato italiano (dd/mm/yyyy)
      if (dateString.includes('/')) {
        const [day, month, year] = dateString.split('/');
        return `${day}/${month}/${year}`;
      }
      return dateString;
    }
    
    // Formatta la data in italiano (dd/mm/yyyy)
    const day = date.getDate().toString().padStart(2, '0');
    const month = (date.getMonth() + 1).toString().padStart(2, '0');
    const year = date.getFullYear();
    return `${day}/${month}/${year}`;
  } catch (e) {
    return dateString;
  }
};

/**
 * Formatta una data in formato ISO (yyyy-mm-dd) per input HTML
 * @param {string|Date} dateString - La data da formattare
 * @returns {string} - Data in formato ISO
 */
export const formatDateForInput = (dateString) => {
  if (!dateString) return "";
  
  try {
    const date = new Date(dateString);
    if (isNaN(date.getTime())) return "";
    
    const year = date.getFullYear();
    const month = (date.getMonth() + 1).toString().padStart(2, '0');
    const day = date.getDate().toString().padStart(2, '0');
    return `${year}-${month}-${day}`;
  } catch (e) {
    return "";
  }
};

/**
 * Formatta una data con orario in formato italiano
 * @param {string|Date} dateString - La data da formattare
 * @returns {string} - Data e ora formattate
 */
export const formatDateTime = (dateString) => {
  if (!dateString) return "";
  
  try {
    const date = new Date(dateString);
    if (isNaN(date.getTime())) return "";
    
    const day = date.getDate().toString().padStart(2, '0');
    const month = (date.getMonth() + 1).toString().padStart(2, '0');
    const year = date.getFullYear();
    const hours = date.getHours().toString().padStart(2, '0');
    const minutes = date.getMinutes().toString().padStart(2, '0');
    
    return `${day}/${month}/${year} ${hours}:${minutes}`;
  } catch (e) {
    return dateString;
  }
};

/**
 * Formatta solo l'orario
 * @param {string|Date} dateString - La data da cui estrarre l'orario
 * @returns {string} - Orario formattato (HH:mm)
 */
export const formatTime = (dateString) => {
  if (!dateString) return "";
  
  try {
    const date = new Date(dateString);
    if (isNaN(date.getTime())) return "";
    
    const hours = date.getHours().toString().padStart(2, '0');
    const minutes = date.getMinutes().toString().padStart(2, '0');
    
    return `${hours}:${minutes}`;
  } catch (e) {
    return "";
  }
};

/**
 * Calcola l'età da una data di nascita
 * @param {string|Date} birthDate - Data di nascita
 * @returns {number} - Età in anni
 */
export const calculateAge = (birthDate) => {
  if (!birthDate) return 0;
  
  try {
    const birth = new Date(birthDate);
    const today = new Date();
    
    if (isNaN(birth.getTime())) return 0;
    
    let age = today.getFullYear() - birth.getFullYear();
    const monthDiff = today.getMonth() - birth.getMonth();
    
    if (monthDiff < 0 || (monthDiff === 0 && today.getDate() < birth.getDate())) {
      age--;
    }
    
    return Math.max(0, age);
  } catch (e) {
    return 0;
  }
};

/**
 * Calcola i giorni tra due date
 * @param {string|Date} startDate - Data di inizio
 * @param {string|Date} endDate - Data di fine
 * @returns {number} - Numero di giorni (può essere negativo)
 */
export const daysBetween = (startDate, endDate) => {
  if (!startDate || !endDate) return 0;
  
  try {
    const start = new Date(startDate);
    const end = new Date(endDate);
    
    if (isNaN(start.getTime()) || isNaN(end.getTime())) return 0;
    
    const diffTime = end - start;
    const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));
    
    return diffDays;
  } catch (e) {
    return 0;
  }
};

/**
 * Controlla se una data è oggi
 * @param {string|Date} date - Data da controllare
 * @returns {boolean} - true se è oggi
 */
export const isToday = (date) => {
  if (!date) return false;
  
  try {
    const checkDate = new Date(date);
    const today = new Date();
    
    if (isNaN(checkDate.getTime())) return false;
    
    return checkDate.toDateString() === today.toDateString();
  } catch (e) {
    return false;
  }
};

/**
 * Controlla se una data è nel passato
 * @param {string|Date} date - Data da controllare
 * @returns {boolean} - true se è nel passato
 */
export const isPastDate = (date) => {
  if (!date) return false;
  
  try {
    const checkDate = new Date(date);
    const today = new Date();
    
    if (isNaN(checkDate.getTime())) return false;
    
    today.setHours(0, 0, 0, 0);
    checkDate.setHours(0, 0, 0, 0);
    
    return checkDate < today;
  } catch (e) {
    return false;
  }
};

/**
 * Controlla se una data è nel futuro
 * @param {string|Date} date - Data da controllare
 * @returns {boolean} - true se è nel futuro
 */
export const isFutureDate = (date) => {
  if (!date) return false;
  
  try {
    const checkDate = new Date(date);
    const today = new Date();
    
    if (isNaN(checkDate.getTime())) return false;
    
    today.setHours(23, 59, 59, 999);
    checkDate.setHours(0, 0, 0, 0);
    
    return checkDate > today;
  } catch (e) {
    return false;
  }
};

/**
 * Aggiunge/sottrae giorni a una data
 * @param {string|Date} date - Data di partenza
 * @param {number} days - Giorni da aggiungere (positivo) o sottrarre (negativo)
 * @returns {Date} - Nuova data
 */
export const addDays = (date, days) => {
  if (!date) return null;
  
  try {
    const result = new Date(date);
    if (isNaN(result.getTime())) return null;
    
    result.setDate(result.getDate() + days);
    return result;
  } catch (e) {
    return null;
  }
};

/**
 * Restituisce il primo giorno del mese
 * @param {string|Date} date - Data di riferimento
 * @returns {Date} - Primo giorno del mese
 */
export const getFirstDayOfMonth = (date = new Date()) => {
  try {
    const d = new Date(date);
    return new Date(d.getFullYear(), d.getMonth(), 1);
  } catch (e) {
    return new Date();
  }
};

/**
 * Restituisce l'ultimo giorno del mese
 * @param {string|Date} date - Data di riferimento
 * @returns {Date} - Ultimo giorno del mese
 */
export const getLastDayOfMonth = (date = new Date()) => {
  try {
    const d = new Date(date);
    return new Date(d.getFullYear(), d.getMonth() + 1, 0);
  } catch (e) {
    return new Date();
  }
};

/**
 * Formatta una data in formato relativo (es: "2 giorni fa", "tra 1 settimana")
 * @param {string|Date} date - Data da formattare
 * @returns {string} - Descrizione relativa
 */
export const formatRelativeDate = (date) => {
  if (!date) return "";
  
  try {
    const targetDate = new Date(date);
    const now = new Date();
    
    if (isNaN(targetDate.getTime())) return "";
    
    const diffMs = targetDate - now;
    const diffDays = Math.floor(diffMs / (1000 * 60 * 60 * 24));
    
    if (diffDays === 0) return "Oggi";
    if (diffDays === 1) return "Domani";
    if (diffDays === -1) return "Ieri";
    if (diffDays > 1 && diffDays <= 7) return `Tra ${diffDays} giorni`;
    if (diffDays < -1 && diffDays >= -7) return `${Math.abs(diffDays)} giorni fa`;
    if (diffDays > 7) {
      const weeks = Math.floor(diffDays / 7);
      return weeks === 1 ? "Tra 1 settimana" : `Tra ${weeks} settimane`;
    }
    if (diffDays < -7) {
      const weeks = Math.floor(Math.abs(diffDays) / 7);
      return weeks === 1 ? "1 settimana fa" : `${weeks} settimane fa`;
    }
    
    return formatDate(date);
  } catch (e) {
    return "";
  }
};

/**
 * Nomi dei mesi in italiano
 */
export const monthNames = [
  "Gennaio", "Febbraio", "Marzo", "Aprile", "Maggio", "Giugno",
  "Luglio", "Agosto", "Settembre", "Ottobre", "Novembre", "Dicembre"
];

/**
 * Nomi dei giorni della settimana in italiano
 */
export const weekDayNames = [
  "Domenica", "Lunedì", "Martedì", "Mercoledì", "Giovedì", "Venerdì", "Sabato"
];

/**
 * Formatta una data con il nome del mese
 * @param {string|Date} date - Data da formattare
 * @returns {string} - Data con nome del mese (es: "15 Marzo 2024")
 */
export const formatDateWithMonthName = (date) => {
  if (!date) return "";
  
  try {
    const d = new Date(date);
    if (isNaN(d.getTime())) return "";
    
    const day = d.getDate();
    const month = monthNames[d.getMonth()];
    const year = d.getFullYear();
    
    return `${day} ${month} ${year}`;
  } catch (e) {
    return "";
  }
};

export default {
  formatDate,
  formatDateForInput,
  formatDateTime,
  formatTime,
  calculateAge,
  daysBetween,
  isToday,
  isPastDate,
  isFutureDate,
  addDays,
  getFirstDayOfMonth,
  getLastDayOfMonth,
  formatRelativeDate,
  formatDateWithMonthName,
  monthNames,
  weekDayNames
};